import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import VariablesTable from './variablesTable';
import Chart from './chart';
import CombinedChart from './combinedChart';
import EmptyChart from './emptyChart';
import Loading from '../../main/components/loading';
import { getChartTitle, getFileName, mapDataToSerie } from '../utils';
import ValuesTable from './valuesTable';
import { idsTypeIds } from '../constants';

const CompareExperimentsCharts = ({
  error,
  loading,
  chartsData,
  handleRemoveClick,
  selectedVariableName,
  selectedVariableDefinition,
  selectedVariableUnit,
  selectedExperimentsCount,
  showCombinedChart
}) => {
  const { t } = useTranslation();

  if (error) {
    return <h6 className='text-center mt-3'>{error}</h6>;
  }
  else if (loading) {
    return <Loading className='text-center mt-3' />;
  }
  else if (chartsData.length > 0) {
    if (chartsData.some(({ data }) => data && data.is_time_dependent === false)) {
      return <VariablesTable chartsData={chartsData} />;
    }
    else {
      return (
        <section className='p-3'>
          <div className='chart-transition-wrapper'>
            {/* Combined View */}
            <div className={`chart-view-content combined ${showCombinedChart ? 'active' : 'inactive'}`}>
              <Row>
                <Col md='12' className={"mb-3"}>
                  <CombinedChart
                    chartsData={chartsData}
                    selectedVariableName={selectedVariableName}
                    selectedVariableDefinition={selectedVariableDefinition}
                    selectedVariableUnit={selectedVariableUnit}
                    handleRemoveClick={handleRemoveClick}
                  />
                </Col>
              </Row>
            </div>
            {/* Individual View */}
            <div className={`chart-view-content individual ${!showCombinedChart ? 'active' : 'inactive'}`}>
              <Row>
                {chartsData.map(({ data, experimentInfo: { pulse, run, machine }, id }, index) => (
                  data ? (
                    <Col key={pulse} md='6' className={"mb-3"}>
                      { data.idsTypeId !== idsTypeIds.pedestal ? <Chart
                        data={mapDataToSerie(data.data?.[0])}
                        title={getChartTitle(pulse, run, machine, selectedVariableName)}
                        description={selectedVariableDefinition}
                        unit={selectedVariableUnit}
                        titleAlignment='top'
                        fileName={getFileName(data.ids_field, pulse, run, machine)}
                        width={553}
                        height={260}
                        showCloseBtn={selectedExperimentsCount > 2}
                        onClose={() => handleRemoveClick(id)}
                      /> :
                        <>
                          <span>{getChartTitle(pulse, run, machine, selectedVariableName)}</span>
                          <ValuesTable groupedEntryData={mapDataToSerie(data.data?.[0])} />
                        </> }
                    </Col>
                  ) : (
                    <Col key={pulse} md='6' className={"mb-3"}>
                      <EmptyChart
                        title={getChartTitle(pulse, run, machine, selectedVariableName)}
                        noDataMessage={t('compare_experiments_no_data_message')}
                        width={553}
                        height={260}
                      />
                    </Col>
                  )
                ))}
              </Row>
            </div>
          </div>
        </section>
      );
    }
  }
  else {
    return null;
  }
};

CompareExperimentsCharts.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  chartsData: PropTypes.array,
  handleRemoveClick: PropTypes.func,
  selectedVariableName: PropTypes.string,
  selectedVariableDefinition: PropTypes.string,
  selectedVariableUnit: PropTypes.string,
  selectedExperimentsCount: PropTypes.number,
  showCombinedChart: PropTypes.bool
};

export default CompareExperimentsCharts;
